import { Injectable } from '@angular/core';
import { CONTRIBUTOR, DATA_ADMIN, ElasticApiResponse, ELUCIDATA_DEFAULT_ROLE, 
  FrontendOrganizationInfo, Hit, JupyterConfiguration, License, LicenseBasedAppList, ORG_ADMIN, SUPER_ADMIN, User, 
  UserResponse, UserRole, UserRoleMap
} from 'polly-commons';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { ComputeApplicationData, ESWorkspaceAttributes, ESWorkspacePermission, WorkspaceContentItem, WorkspaceContentMetaData, WorkspaceFileFilterParameters } from '../../interfaces';

@Injectable({
  providedIn: 'root',
})
export class PollyManageDataService {
  public allWebApps: ComputeApplicationData[] | undefined = undefined;
  public allComponents: ComputeApplicationData[] | undefined = undefined;
  public workspaceList$: Observable<ElasticApiResponse<ESWorkspaceAttributes, undefined>>;
  public totalWorkspacesCount$: Observable<number>;
  public selectedWorkspace$: Observable<ESWorkspaceAttributes | null>;
  public currentFilePath$: Observable<string>;
  public currentFolderPath$: Observable<string>;
  public workspaceContentArray$: Observable<Hit<WorkspaceContentItem>[]>;
  public selectedWorkspaceItems$: Observable<Hit<WorkspaceContentItem>[]>;
  public currentUserOrgInfo$: Observable<FrontendOrganizationInfo | null>;
  public currentUser$: Observable<User | null>;
  public currentUserRole$: Observable<UserRole | null>;
  public currentUserRoleForOmixAtlas$: Observable<UserRole | null>;
  public currentUserLicense$: Observable<License | null>;
  public currentSelectedWorkspaceFile$: Observable<Hit<WorkspaceContentItem> | null>;
  public currentWorkspaceCollaboratorsList$: Observable<ESWorkspacePermission[]>;
  public workspaceSearchInput$: Observable<string>;
  public workspaceContentSearchInput$: Observable<string>;
  public workspaceContentNextUrls$: Observable<string[]>;
  public isWorkspacePanelClosed$: Observable<boolean>;
  public isDetailsPanelClosed$: Observable<boolean>;
  public workspaceMetaData$: Observable<WorkspaceContentMetaData[]>;
  public currentUserWorkspacePermission$: Observable<string>;
  public workspacePinnedItemList$: Observable<Hit<WorkspaceContentItem>[]>;
  public jupyterConfig$: Observable<JupyterConfiguration | null>;
  public userResponse$: Observable<UserResponse | null>;
  public isFileRenamed$: Observable<boolean>;
  public workspaceContentCount$: Observable<number>;
  public isInternalUser: boolean = false;


  public filter$: ReplaySubject<WorkspaceFileFilterParameters> = new ReplaySubject<WorkspaceFileFilterParameters>(1);
  public readonly currentUser: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  public readonly currentUserOrg: BehaviorSubject<FrontendOrganizationInfo | null> = new BehaviorSubject<FrontendOrganizationInfo | null>(null);

  private readonly userResponse: BehaviorSubject<UserResponse | null> = new BehaviorSubject<UserResponse | null>(null);
  private readonly currentWorkspaceCollaboratorsList: ReplaySubject<ESWorkspacePermission[]> = new ReplaySubject<ESWorkspacePermission[]>(
    1
  );
  private readonly workspaceList: ReplaySubject<ElasticApiResponse<ESWorkspaceAttributes, undefined>> = new ReplaySubject(1);
  private readonly workspacePinnedItemList: BehaviorSubject<Hit<WorkspaceContentItem>[]> = new BehaviorSubject<Hit<WorkspaceContentItem>[]>(
    []
  );
  private readonly totalWorkspacesCount: ReplaySubject<number> = new ReplaySubject(1);

  private readonly selectedWorkspace: BehaviorSubject<ESWorkspaceAttributes | null> = new BehaviorSubject<ESWorkspaceAttributes | null>(
    null
  );
  private readonly currentFilePath: Subject<string> = new BehaviorSubject<string>('');
  private readonly currentFolderPath: BehaviorSubject<string> = new BehaviorSubject<string>('');
  // This is used for storing multiple selected items
  private readonly selectedWorkspaceItems: ReplaySubject<Hit<WorkspaceContentItem>[]> = new ReplaySubject();
  // This is used for displaying workspace contents
  private readonly workspaceContentArray: BehaviorSubject<Hit<WorkspaceContentItem>[]> = new BehaviorSubject<Hit<WorkspaceContentItem>[]>(
    []
  );
  private readonly currentUserLicense: BehaviorSubject<License | null> = new BehaviorSubject<License | null>(null);
  // This will store the highest priority role for workspaces
  private readonly userRole: BehaviorSubject<UserRole | null> = new BehaviorSubject<UserRole | null>(null);
  // This will store the highest priority role for omixatlases
  private readonly userRoleOmixAtlas: BehaviorSubject<UserRole | null> = new BehaviorSubject<UserRole | null>(null);
  // This will store all the roles user has
  private readonly userRoles: BehaviorSubject<UserRoleMap | null> = new BehaviorSubject<UserRoleMap | null>(null);
  private readonly currentSelectedWorkspaceFile: BehaviorSubject<Hit<WorkspaceContentItem> | null> =
    new BehaviorSubject<Hit<WorkspaceContentItem> | null>(null);
  private readonly workspaceSearchInput: Subject<string> = new Subject<string>();
  private readonly workspaceContentSearchInput: Subject<string> = new BehaviorSubject<string>('');
  private readonly workspaceContentNextUrls: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private readonly isWorkspacePanelClosed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly isDetailsPanelClosed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private readonly isFileRenamed: Subject<boolean> = new Subject<boolean>();
  private readonly workspaceMetaData: BehaviorSubject<WorkspaceContentMetaData[]> = new BehaviorSubject<WorkspaceContentMetaData[]>([]);
  private readonly currentUserWorkspacePermission: ReplaySubject<string> = new ReplaySubject<string>(1);
  private readonly jupyterConfig: BehaviorSubject<JupyterConfiguration | null> = new BehaviorSubject<JupyterConfiguration | null>(null);
  private readonly workspaceContentCount: BehaviorSubject<number> = new BehaviorSubject<number>(1);

  constructor() {
    this.currentUser$ = this.currentUser.asObservable();
    this.currentUserOrgInfo$ = this.currentUserOrg.asObservable();
    this.currentUserRole$ = this.userRole.asObservable();
    this.currentUserRoleForOmixAtlas$ = this.userRoleOmixAtlas.asObservable();
    this.workspaceList$ = this.workspaceList.asObservable();
    this.totalWorkspacesCount$ = this.totalWorkspacesCount.asObservable();
    this.selectedWorkspace$ = this.selectedWorkspace.asObservable();
    this.currentFilePath$ = this.currentFilePath.asObservable();
    this.currentFolderPath$ = this.currentFolderPath.asObservable();
    this.workspaceContentArray$ = this.workspaceContentArray.asObservable();
    this.selectedWorkspaceItems$ = this.selectedWorkspaceItems.asObservable();
    this.currentUserLicense$ = this.currentUserLicense.asObservable();
    this.currentSelectedWorkspaceFile$ = this.currentSelectedWorkspaceFile.asObservable();
    this.currentWorkspaceCollaboratorsList$ = this.currentWorkspaceCollaboratorsList.asObservable();
    this.workspaceSearchInput$ = this.workspaceSearchInput.asObservable();
    this.workspaceContentSearchInput$ = this.workspaceContentSearchInput.asObservable();
    this.workspaceContentNextUrls$ = this.workspaceContentNextUrls.asObservable();
    this.isWorkspacePanelClosed$ = this.isWorkspacePanelClosed.asObservable();
    this.isDetailsPanelClosed$ = this.isDetailsPanelClosed.asObservable();
    this.workspaceMetaData$ = this.workspaceMetaData.asObservable();
    this.currentUserWorkspacePermission$ = this.currentUserWorkspacePermission.asObservable();
    this.jupyterConfig$ = this.jupyterConfig.asObservable();
    this.workspacePinnedItemList$ = this.workspacePinnedItemList.asObservable();
    this.userResponse$ = this.userResponse.asObservable();
    this.isFileRenamed$ = this.isFileRenamed.asObservable();
    this.workspaceContentCount$ = this.workspaceContentCount;
  }
  // setters
  public setWorkspacePinnedItemList(data: Hit<WorkspaceContentItem>[]): void {
    this.workspacePinnedItemList.next(data);
  }
  public setCollaboratorsList(data: ESWorkspacePermission[]): void {
    this.currentWorkspaceCollaboratorsList.next(data);
  }
  public setWorkspaceList(data: ElasticApiResponse<ESWorkspaceAttributes, undefined>): void {
    this.workspaceList.next(data);
  }
  public setSelectedWorkspace(data: ESWorkspaceAttributes | null): void {
    this.selectedWorkspace.next(data);
  }
  public setCurrentFilePath(data: string): void {
    this.currentFilePath.next(data);
  }
  public setCurrentFolderPath(data: string): void {
    this.currentFolderPath.next(data);
  }
  public setWorkspaceContentArray(data: Hit<WorkspaceContentItem>[]): void {
    this.workspaceContentArray.next(data);
  }

  public addWorkspaceContentItem(item: Hit<WorkspaceContentItem>): void {
    const items: Hit<WorkspaceContentItem>[] = this.workspaceContentArray.getValue();
    items.unshift(item);
    this.workspaceContentArray.next(items);
  }

  public setSelectedWorkspaceItems(data: Hit<WorkspaceContentItem>[]): void {
    this.selectedWorkspaceItems.next(data);
  }

  public set currentUserInfo(user: User) {
    this.currentUser.next(user);
  }

  public set currentUserOrganization(orgInfo: FrontendOrganizationInfo) {
    this.currentUserOrg.next(orgInfo);
  }

  public set userInfo(userInfo: UserResponse) {
    this.userResponse.next(userInfo);
  }

  public set currentUserLicenseInfo(license: License) {
    this.currentUserLicense.next(license);
  }

  public set currentUserRole(userRole: UserRole) {
    this.userRole.next(userRole);
  }

  public set currentUserRoleForOmixAtlas(userRole: UserRole) {
    this.userRoleOmixAtlas.next(userRole);
  }

  public set currentUserRoles(userRoleMap: UserRoleMap) {
    this.userRoles.next(userRoleMap);
    this.isInternalUser = !!(userRoleMap && userRoleMap[ELUCIDATA_DEFAULT_ROLE]);
  }

  public getCurrentUserOrg(): FrontendOrganizationInfo | null {
    return this.currentUserOrg.getValue();
  }

  public getUserInfo(): UserResponse | null {
    return this.userResponse.getValue();
  }

  public getCurrentUserRoles(): UserRoleMap | null {
    return this.userRoles.getValue();
  }

  public getCurrentUserRoleForOmixAtlas(): UserRole | null {
    return this.userRoleOmixAtlas.getValue();
  }

  public set workspacesCount(workspaceCount: number) {
    this.totalWorkspacesCount.next(workspaceCount);
  }

  // this setter would set the file selected in workspace
  public set CurrentWorkspaceFileInfo(file: Hit<WorkspaceContentItem> | null) {
    this.currentSelectedWorkspaceFile.next(file);
  }

  // sets value of allAppsWfsComponent
  public setAllComponents(value: ComputeApplicationData[]): void {
    this.allComponents = value;
  }

  // sets value of allWebAppComponent
  public setAllWebApps(value: ComputeApplicationData[]): void {
    this.allWebApps = value;
  }

  // sets workspace search keyword
  public setWorkspaceSearchInput(searchInput: string): void {
    this.workspaceSearchInput.next(searchInput);
  }

  // sets workspace content search keyword
  public setWorkspaceContentSearchInput(searchInput: string): void {
    this.workspaceContentSearchInput.next(searchInput);
  }

  public setDetailsPanelToggle(value: boolean): void {
    this.isDetailsPanelClosed.next(value);
  }

  /**
   * @deprecated Using newer ES APIs for metadata
   * @param data metadata of workspace contents
   */
  public setWorkspaceContentMetaData(data: WorkspaceContentMetaData[]): void {
    this.workspaceMetaData.next(data);
  }

  public set setCurrentUserWorkspacePermission(data: string) {
    this.currentUserWorkspacePermission.next(data);
  }

  public setJupyterConfig(jupyterConfig: JupyterConfiguration): void {
    this.jupyterConfig.next(jupyterConfig);
  }

  public set setWorkspaceContentCount(contentCount: number) {
    this.workspaceContentCount.next(contentCount);
  }

  // getters
  public getCurrentWorkspace(): ESWorkspaceAttributes | null {
    return this.selectedWorkspace.getValue();
  }

  // getter for current user info
  public getCurrentUser(): User | null {
    return this.currentUser.getValue();
  }

  public getCurrentUserRole(): UserRole | null {
    return this.userRole.getValue();
  }

  public getCurrentWorkspaceFile(): Hit<WorkspaceContentItem> | null {
    return this.currentSelectedWorkspaceFile.getValue();
  }

  public getJupyterConfig(): JupyterConfiguration | null {
    return this.jupyterConfig.getValue();
  }

  public getCurrentUserLicenseInfo(): License | null {
    return this.currentUserLicense.getValue();
  }

  public getWorkspaceContentCount(): number {
    return this.workspaceContentCount.getValue();
  }

  // This function clear workspace files filters
  public clearWorkspaceFilesFilters(): void {
    this.filter$.next({
      showAnalysis: false,
      showFiles: false,
      showFolders: false,
      showNotebooks: false,
      filtersCleared: true,
    });
  }

  /**
   * This array defines the priority of Roles
   * @returns Roles by Priority For Workspaces(Highest first)
   */
  public getAdminRolesByPriorityForWorkspaces(): string[] {
    return [SUPER_ADMIN, ORG_ADMIN, DATA_ADMIN, CONTRIBUTOR];
  }

  /**
   * This array defines the priority of Roles
   * @returns Roles by Priority For OmixAtlases(Highest first)
   */
  public getAdminRolesByPriorityForOmixAtlases(): string[] {
    return [SUPER_ADMIN, DATA_ADMIN, ORG_ADMIN, CONTRIBUTOR];
  }

  /**
   * Whether Curation App is available or not.
   */
  public isCurationAppAvailable(): boolean {
    return false;
  }

  public isCurationAdmin(): boolean {
    const userRole = this.getCurrentUserRoleForOmixAtlas();
    if (!userRole) return false;
    return false;
  }

  public isAppAvailable(appName: LicenseBasedAppList): boolean {
    const licenseInfo = this.currentUserLicense.getValue();
    return (licenseInfo && licenseInfo.attributes.modules && licenseInfo.attributes.modules[appName]) || false;
  }

  public fileRenameSucess(isRenamed: boolean): void {
    this.isFileRenamed.next(isRenamed);
  }
}
